import {
  CurrencyValue,
  formatNumber,
  formatPercentage,
  LineChart,
  formatDatetime
} from '@blockanalitica/ui';
import { useMemo, useCallback } from 'react';
import { farmMapping } from '@/utils/farms';
import { useChartPeriod } from '@/hooks';
import { ExtendedChartOptions } from '@blockanalitica/ui/package/types/src/components/charts/Chart';

export default function HistoricTabs({
  data,
  timeFilter,
  address,
  chartDaysAgo,
  activeDisplayOption
}) {
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);

  const config = updateChartConfigWithPeriod(
    activeDisplayOption === 'boosted_rewards' ||
      activeDisplayOption === 'total_farmed'
      ? { options: { fill: false } as ExtendedChartOptions }
      : {}
  );

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );
  const valueFormatter = useMemo(() => {
    function formatValue(value: string) {
      switch (activeDisplayOption) {
        case 'boosted_rewards':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: {
                  name: data[0].rewards_token_symbol,
                  address: data[0].rewards_token_address
                },
                iconSize: '0'
              }}
            />
          );
        case 'apy':
          return `${formatPercentage(Number(value))}`;
        case 'total_farmed':
          return farmMapping(address) !== 'chronicle_farm' ? (
            <CurrencyValue
              value={value}
              options={{
                currency: {
                  name: data[0].rewards_token_symbol,
                  address: data[0].rewards_token_address
                },
                iconSize: '0'
              }}
            />
          ) : (
            <CurrencyValue value={value} />
          );
        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: {
                  name: data[0].staking_token_symbol,
                  address: data[0].staking_token_address
                },
                iconSize: '3'
              }}
            />
          );
      }
    }

    return formatValue;
  }, [activeDisplayOption, data, address]);

  const yScaleValueFormatter = useMemo(() => {
    return (value) => {
      switch (activeDisplayOption) {
        case 'apy':
          return `${formatPercentage(value)}`;
        default:
          return `${formatNumber(value)}`;
      }
    };
  }, [activeDisplayOption]);

  const datasets = useMemo(() => {
    if (
      activeDisplayOption === 'total_farmed' &&
      farmMapping(address) === 'chronicle_farm'
    ) {
      return [
        {
          xField: 'date',
          yField: activeDisplayOption
        }
      ];
    }

    switch (activeDisplayOption) {
      case 'boosted_rewards':
        return [
          {
            label: 'Boosted Rewards',
            borderColor: 'rgba(255, 139, 91, 1)',
            xField: 'date',
            yField: 'boosted_rewards_total'
          },
          {
            label: 'Boosted Rewards Claimed',
            borderColor: 'rgba(251, 200, 84, 1)',
            xField: 'date',
            yField: 'boosted_rewards_claimed'
          },
          {
            label: 'Boosted Rewards Unclaimed',
            borderColor: 'rgba(100, 150, 250, 1)',
            xField: 'date',
            yField: 'boosted_rewards_unclaimed'
          }
        ];
      case 'total_farmed':
        return [
          {
            label: 'Rewards',
            borderColor: 'rgba(255, 139, 91, 1)',
            xField: 'date',
            yField: 'total_farmed'
          },
          {
            label: 'Claimed Rewards',
            borderColor: 'rgba(251, 200, 84, 1)',
            xField: 'date',
            yField: 'claimed'
          },
          {
            label: 'Unclaimed Rewards',
            borderColor: 'rgba(100, 150, 250, 1)',
            xField: 'date',
            yField: 'unclaimed'
          }
        ];
      default:
        return [
          {
            xField: 'date',
            yField: activeDisplayOption
          }
        ];
    }
  }, [activeDisplayOption, address]);

  return (
    <LineChart
      key={activeDisplayOption}
      data={data}
      valueFormatter={valueFormatter}
      datasets={datasets}
      yScaleValueFormatter={yScaleValueFormatter}
      timeFilter={timeFilter}
      config={config}
      subValueFormatter={subValueFormatter}
    />
  );
}
